import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { MenuEditor, CareersEditor } from './WebsiteMenuCareers'; // @@MENU_CAREERS_v89@@
import WebsiteTraffic from './WebsiteTraffic'; // @@SITE_ANALYTICS_v92@@
import WebsiteInquiries from './WebsiteInquiries'; // @@FORM_SPAM_v96@@ Inbox + Spam

// @@SITE_v85@@ HQ › Website — edit + publish the public site (xyzmerch.com).
// Edits save to a DRAFT; Publish makes the draft live (the site service picks
// it up within ~30s). Admin/manager only (route-guarded in App.js; the server
// re-checks via requireRole).

const TABS = [
  { key: 'content', label: 'Sections' },
  { key: 'menu', label: 'Menu' }, // @@MENU_CAREERS_v89@@
  { key: 'stores', label: 'Stores' },
  { key: 'clients', label: 'Clients served' },
  { key: 'careers', label: 'Careers page' }, // @@MENU_CAREERS_v89@@
  { key: 'images', label: 'Images' },
  { key: 'inquiries', label: 'Inquiries' },
  { key: 'traffic', label: 'Traffic' }, // @@SITE_ANALYTICS_v92@@
];
const ICONS = ['store', 'shirt', 'truck', 'pen', 'portal', 'box', 'star', 'globe'];
const DEFAULT_IMAGES = [
  { id: 'default-pano', label: 'Warehouse panorama (built-in)' },
  { id: 'default-overhead', label: 'Warehouse overhead (built-in)' },
];

const LINK = { color: '#31b44b', textDecoration: 'none' };

function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }

const S = {
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.06em' },
  input: { width: '100%', boxSizing: 'border-box' },
  area: { width: '100%', boxSizing: 'border-box', height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 },
  body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
  row: { display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', padding: '12px 0', borderBottom: '1px solid var(--hq-line)' },
  small: { height: 32, padding: '0 10px', fontSize: 12 },
};

function Field({ label, value, onChange, multiline, rows = 3, max, type = 'text' }) {
  return (
    <label style={S.label}>{label}
      {multiline
        ? <textarea className="hq-input" style={S.area} rows={rows} maxLength={max} value={value || ''} onChange={(e) => onChange(e.target.value)} />
        : <input className="hq-input" style={S.input} type={type} maxLength={max} value={value || ''} onChange={(e) => onChange(e.target.value)} />}
    </label>
  );
}

function ImagePicker({ label, value, assets, onChange, allowNone }) {
  return (
    <label style={S.label}>{label}
      <select className="hq-select" style={S.input} value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
        {allowNone && <option value="">No logo (name only)</option>}
        {!allowNone && DEFAULT_IMAGES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        {assets.map((a) => <option key={a.id} value={a.id}>{a.filename || a.id.slice(0, 8)}</option>)}
      </select>
    </label>
  );
}

// Generic ordered-list editor (stats, service cards, steps, …).
function ListEditor({ items, onChange, fields, blank, max, title }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= items.length) return;
    const next = items.slice(); [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  };
  return (
    <div>
      <div style={{ ...S.label, marginBottom: 4 }}>{title}</div>
      {items.map((it, i) => (
        <div key={i} style={S.row}>
          <div style={{ ...S.grid2, flex: '1 1 400px' }}>
            {fields.map((f) => (
              f.type === 'select'
                ? <label key={f.key} style={S.label}>{f.label}
                    <select className="hq-select" style={S.input} value={it[f.key] || ''} onChange={(e) => set(i, f.key, e.target.value)}>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                : <Field key={f.key} label={f.label} value={it[f.key]} max={f.max} multiline={f.multiline} rows={2}
                    onChange={(v) => set(i, f.key, v)} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, paddingTop: 20 }}>
            <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0 }} aria-label="Move up" onClick={() => move(i, -1)}>↑</button>
            <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0 }} aria-label="Move down" onClick={() => move(i, 1)}>↓</button>
            <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0, color: '#ff7a73' }} onClick={() => onChange(items.filter((_, j) => j !== i))}>Remove</button>
          </div>
        </div>
      ))}
      {items.length < max && (
        <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px' }} onClick={() => onChange([...items, { ...blank }])}>+ Add</button>
      )}
    </div>
  );
}

function Panel({ title, show, onToggle, children }) {
  return (
    <div className="hq-panel" style={{ marginBottom: 16 }}>
      <div className="hq-panel-head">
        <h2>{title}</h2>
        {onToggle && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--hq-muted)' }}>
            <input type="checkbox" checked={show} onChange={(e) => onToggle(e.target.checked)} /> Show on site
          </label>
        )}
      </div>
      <div style={S.body}>{children}</div>
    </div>
  );
}

export default function Website() {
  const [meta, setMeta] = useState(null);      // server view (publish info)
  const [saved, setSaved] = useState(null);    // last saved draft
  const [draft, setDraft] = useState(null);    // working copy
  const [assets, setAssets] = useState([]);
  const [tab, setTab] = useState('content');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const dirty = useMemo(() => draft && saved && JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);

  function apply(d) { setMeta(d); setSaved(d.draft); setDraft(d.draft); }

  async function load() {
    const res = await apiFetch('/api/site');
    const d = await res.json().catch(() => ({}));
    if (res.ok) apply(d); else setMsg(d.error || 'Could not load the website content.');
  }
  async function loadAssets() {
    const res = await apiFetch('/api/site/assets');
    if (res.ok) { const d = await res.json(); setAssets(d.assets || []); }
  }
  useEffect(() => { load(); loadAssets(); }, []);

  // Warn before leaving with unsaved edits.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const up = (section, key, value) => setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));
  const toggle = (key, v) => setDraft((d) => ({ ...d, sections: { ...d.sections, [key]: v } }));

  async function save() {
    setMsg(''); setBusy('save');
    try {
      const res = await apiFetch('/api/site', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content: draft }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { apply(d); setMsg('Draft saved.'); return true; }
      setMsg(d.error || 'Could not save.'); return false;
    } finally { setBusy(''); }
  }
  async function publish() {
    if (dirty && !(await save())) return;
    if (!window.confirm('Publish the draft to xyzmerch.com? It goes live within about 30 seconds.')) return;
    setBusy('publish');
    try {
      const res = await apiFetch('/api/site/publish', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { apply(d); setMsg('Published. The live site updates within ~30 seconds.'); } else setMsg(d.error || 'Could not publish.');
    } finally { setBusy(''); }
  }
  async function discard() {
    if (!window.confirm('Discard all unpublished changes and go back to what is live?')) return;
    const res = await apiFetch('/api/site/discard', { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { apply(d); setMsg('Draft reset to the live site.'); } else setMsg(d.error || 'Could not discard.');
  }
  async function preview() {
    if (dirty && !(await save())) return;
    const win = window.open('', '_blank'); // open synchronously so popup blockers allow it
    const res = await apiFetch(tab === 'careers' ? '/api/site/preview?page=careers' : '/api/site/preview'); // @@MENU_CAREERS_v89@@
    if (!res.ok) { if (win) win.close(); setMsg('Could not build the preview.'); return; }
    const blob = new Blob([await res.text()], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    if (win) win.location.href = url; else window.location.href = url;
  }
  async function upload(file) {
    if (!file) return null;
    if (file.size > 3 * 1024 * 1024) { setMsg('Images must be 3 MB or smaller.'); return null; }
    setBusy('upload');
    try {
      const res = await apiFetch('/api/site/assets', {
        method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': file.name.replace(/[^\w.\- ]/g, '') },
        body: file,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(d.error || 'Upload failed.'); return null; }
      setAssets((a) => [d.asset, ...a]); setMsg('Image uploaded.');
      return d.asset;
    } finally { setBusy(''); }
  }
  async function removeAsset(a) {
    if (!window.confirm('Delete this image?')) return;
    const res = await apiFetch('/api/site/assets/' + a.id, { method: 'DELETE' });
    const d = await res.json().catch(() => ({}));
    if (res.ok) setAssets((x) => x.filter((y) => y.id !== a.id)); else setMsg(d.error || 'Could not delete.');
  }

  if (!draft) {
    return <div className="hq-page-content"><div className="hq-page-head"><h1>Website</h1><p>{msg || 'Loading…'}</p></div></div>;
  }

  const live = meta.is_default ? 'Not published yet — the site is showing the built-in default content.'
    : `Live since ${fmt(meta.published_at)}${meta.published_by ? ' · ' + meta.published_by : ''}`;
  const pending = dirty || meta.has_unpublished;
  const storeItems = draft.stores.items;
  const setStores = (items) => up('stores', 'items', items);

  return (
    <div className="hq-page-content">
      <div className="hq-page-head" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1>Website</h1>
          <p>
            <a href={meta.site_url} target="_blank" rel="noopener noreferrer" style={LINK}>{meta.site_url.replace(/^https?:\/\//, '')}</a> · {live}
            {pending && <span className="hq-pill hq-off" style={{ marginLeft: 10 }}>{dirty ? 'Unsaved edits' : 'Unpublished draft'}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px', marginTop: 0 }} onClick={preview}>{tab === 'careers' ? 'Preview careers page' : 'Preview draft'}</button>
          {meta.has_unpublished && !meta.is_default && !dirty && (
            <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px', marginTop: 0 }} onClick={discard}>Discard draft</button>
          )}
          <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px', marginTop: 0 }} disabled={!dirty || !!busy} onClick={save}>
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </button>
          <button type="button" className="hq-btn" disabled={!!busy || (!pending && !meta.is_default)} onClick={publish}>
            {busy === 'publish' ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
      {msg && <p className="hq-msg" style={{ marginTop: -12, marginBottom: 16 }}>{msg}</p>}

      <div role="tablist" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, borderBottom: '1px solid var(--hq-line)', marginBottom: 20 }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            style={{ height: 42, padding: '0 14px', background: 'none', border: 0, borderBottom: tab === t.key ? '2px solid #31b44b' : '2px solid transparent', color: tab === t.key ? 'var(--hq-txt)' : 'var(--hq-muted)', font: 'inherit', fontSize: 14, cursor: 'pointer' }}>
            {t.label}{t.key === 'stores' ? ` (${storeItems.length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'content' && (
        <>
          <Panel title="Hero">
            <div style={S.grid2}>
              <Field label="Eyebrow" value={draft.hero.eyebrow} max={80} onChange={(v) => up('hero', 'eyebrow', v)} />
              <ImagePicker label="Background photo" value={draft.hero.background} assets={assets} onChange={(v) => up('hero', 'background', v)} />
              <Field label="Headline (white)" value={draft.hero.headline_1} max={80} onChange={(v) => up('hero', 'headline_1', v)} />
              <Field label="Headline (accent)" value={draft.hero.headline_2} max={80} onChange={(v) => up('hero', 'headline_2', v)} />
            </div>
            <Field label="Subhead" multiline value={draft.hero.subhead} max={400} onChange={(v) => up('hero', 'subhead', v)} />
            <div style={S.grid2}>
              <Field label="Primary button" value={draft.hero.cta_primary} max={40} onChange={(v) => up('hero', 'cta_primary', v)} />
              <Field label="Secondary button" value={draft.hero.cta_secondary} max={40} onChange={(v) => up('hero', 'cta_secondary', v)} />
              <label style={S.label}>Accent color
                <input type="color" value={draft.accent} onChange={(e) => setDraft((d) => ({ ...d, accent: e.target.value }))} style={{ height: 38, width: 80, background: 'none', border: 0 }} />
              </label>
            </div>
            <ListEditor title="Proof strip (big number + label)" items={draft.stats} max={6} blank={{ value: '', label: '' }}
              fields={[{ key: 'value', label: 'Value', max: 24 }, { key: 'label', label: 'Label', max: 80 }]}
              onChange={(v) => setDraft((d) => ({ ...d, stats: v }))} />
          </Panel>

          <Panel title="Services grid" show={draft.sections.services} onToggle={(v) => toggle('services', v)}>
            <div style={S.grid2}>
              <Field label="Heading" value={draft.services_intro.heading} max={100} onChange={(v) => up('services_intro', 'heading', v)} />
              <Field label="Intro" value={draft.services_intro.blurb} max={300} onChange={(v) => up('services_intro', 'blurb', v)} />
            </div>
            <ListEditor title="Service cards" items={draft.services} max={9} blank={{ icon: 'star', title: '', body: '' }}
              fields={[{ key: 'icon', label: 'Icon', type: 'select', options: ICONS }, { key: 'title', label: 'Title', max: 60 }, { key: 'body', label: 'Text', multiline: true, max: 400 }]}
              onChange={(v) => setDraft((d) => ({ ...d, services: v }))} />
          </Panel>

          <Panel title="Stores & fulfillment" show={draft.sections.stores_detail} onToggle={(v) => toggle('stores_detail', v)}>
            <Field label="Heading" value={draft.stores_detail.heading} max={100} onChange={(v) => up('stores_detail', 'heading', v)} />
            <Field label="Body (blank line = new paragraph)" multiline rows={5} value={draft.stores_detail.body} max={1200} onChange={(v) => up('stores_detail', 'body', v)} />
            <div style={S.grid2}>
              <Field label="Callout title" value={draft.stores_detail.callout_title} max={80} onChange={(v) => up('stores_detail', 'callout_title', v)} />
              <Field label="Callout text" value={draft.stores_detail.callout_body} max={200} onChange={(v) => up('stores_detail', 'callout_body', v)} />
            </div>
            <ListEditor title="Numbered list" items={draft.stores_detail.items} max={10} blank={{ title: '', body: '' }}
              fields={[{ key: 'title', label: 'Title', max: 80 }, { key: 'body', label: 'Text', multiline: true, max: 400 }]}
              onChange={(v) => up('stores_detail', 'items', v)} />
          </Panel>

          <Panel title="Production" show={draft.sections.production} onToggle={(v) => toggle('production', v)}>
            <div style={S.grid2}>
              <Field label="Heading" value={draft.production.heading} max={100} onChange={(v) => up('production', 'heading', v)} />
              <ImagePicker label="Background photo" value={draft.production.background} assets={assets} onChange={(v) => up('production', 'background', v)} />
            </div>
            <Field label="Intro" multiline value={draft.production.blurb} max={300} onChange={(v) => up('production', 'blurb', v)} />
            <ListEditor title="Cards (leave the big number empty for a text card)" items={draft.production.cards} max={8} blank={{ kicker: '', big: '', sub: '' }}
              fields={[{ key: 'kicker', label: 'Label', max: 40 }, { key: 'big', label: 'Big number', max: 24 }, { key: 'sub', label: 'Detail', max: 160 }]}
              onChange={(v) => up('production', 'cards', v)} />
            <Field label="Promo & specialty line" multiline value={draft.production.promo} max={400} onChange={(v) => up('production', 'promo', v)} />
            <Field label="Terms line" multiline value={draft.production.terms} max={300} onChange={(v) => up('production', 'terms', v)} />
          </Panel>

          <Panel title="Client portal" show={draft.sections.portal} onToggle={(v) => toggle('portal', v)}>
            <Field label="Heading" value={draft.portal.heading} max={100} onChange={(v) => up('portal', 'heading', v)} />
            <Field label="Body" multiline value={draft.portal.body} max={400} onChange={(v) => up('portal', 'body', v)} />
            <ListEditor title="Feature tiles" items={draft.portal.items} max={8} blank={{ title: '', body: '' }}
              fields={[{ key: 'title', label: 'Title', max: 60 }, { key: 'body', label: 'Text', max: 200 }]}
              onChange={(v) => up('portal', 'items', v)} />
          </Panel>

          <Panel title="Contact" show={draft.sections.contact} onToggle={(v) => toggle('contact', v)}>
            <Field label="Heading" value={draft.contact.heading} max={100} onChange={(v) => up('contact', 'heading', v)} />
            <Field label="Body" multiline value={draft.contact.body} max={400} onChange={(v) => up('contact', 'body', v)} />
            <div style={S.grid2}>
              <Field label="Address line 1" value={draft.contact.address_1} max={100} onChange={(v) => up('contact', 'address_1', v)} />
              <Field label="Address line 2" value={draft.contact.address_2} max={100} onChange={(v) => up('contact', 'address_2', v)} />
              <Field label="Public email (optional)" type="email" value={draft.contact.email} max={120} onChange={(v) => up('contact', 'email', v)} />
              <Field label="Public phone (optional)" value={draft.contact.phone} max={40} onChange={(v) => up('contact', 'phone', v)} />
              <Field label="Form button" value={draft.contact.button} max={40} onChange={(v) => up('contact', 'button', v)} />
              <Field label="Footer ASI line" value={draft.footer.asi} max={40} onChange={(v) => up('footer', 'asi', v)} />
            </div>
            <p style={{ margin: 0, color: 'var(--hq-muted)', fontSize: 13 }}>
              Form submissions become <Link to="/leads" style={LINK}>Leads</Link> (source “Website”) and email every admin and manager.
            </p>
          </Panel>

          <Panel title="Banner">
            <p style={{ margin: 0, color: 'var(--hq-muted)', fontSize: 14 }}>
              The site shows the same storefront status banner you manage in <Link to="/settings" style={LINK}>Settings</Link> — no separate setting here.
            </p>
          </Panel>
        </>
      )}

      {tab === 'stores' && (
        <Panel title="Stores we run" show={draft.sections.stores} onToggle={(v) => toggle('stores', v)}>
          <div style={S.grid2}>
            <Field label="Heading" value={draft.stores.heading} max={100} onChange={(v) => up('stores', 'heading', v)} />
            <Field label="Intro" value={draft.stores.blurb} max={300} onChange={(v) => up('stores', 'blurb', v)} />
          </div>
          <div>
            {storeItems.map((s, i) => (
              <div key={i} style={{ ...S.row, alignItems: 'center' }}>
                <div style={{ width: 72, height: 54, borderRadius: 8, background: 'var(--hq-field)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {s.logo ? <img src={`/site-media/assets/${s.logo}`} /* @@SITE_MEDIA_v86_2@@ */ alt="" style={{ maxWidth: 64, maxHeight: 46, objectFit: 'contain' }} />
                    : <span style={{ fontSize: 10, color: 'var(--hq-muted)' }}>no logo</span>}
                </div>
                <input className="hq-input" style={{ flex: '1 1 180px' }} aria-label="Store name" value={s.name} maxLength={60}
                  onChange={(e) => setStores(storeItems.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <input className="hq-input" style={{ flex: '2 1 240px' }} aria-label="Store URL" placeholder="https://…" value={s.url} maxLength={500}
                  onChange={(e) => setStores(storeItems.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} />
                <select className="hq-select" style={{ flex: '1 1 160px' }} aria-label="Logo" value={s.logo || ''}
                  onChange={(e) => setStores(storeItems.map((x, j) => (j === i ? { ...x, logo: e.target.value || null } : x)))}>
                  <option value="">No logo</option>
                  {assets.map((a) => <option key={a.id} value={a.id}>{a.filename || a.id.slice(0, 8)}</option>)}
                </select>
                <label className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  Upload logo
                  <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
                    onChange={async (e) => {
                      const a = await upload(e.target.files[0]); e.target.value = '';
                      if (a) setStores(storeItems.map((x, j) => (j === i ? { ...x, logo: a.id } : x)));
                    }} />
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--hq-muted)' }}>
                  <input type="checkbox" checked={s.visible} onChange={(e) => setStores(storeItems.map((x, j) => (j === i ? { ...x, visible: e.target.checked } : x)))} /> Shown
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: s.featured ? '#31b44b' : 'var(--hq-muted)', fontWeight: s.featured ? 600 : 400 }}> {/* @@FEATURED_STORES_v88@@ */}
                  <input type="checkbox" checked={!!s.featured} onChange={(e) => setStores(storeItems.map((x, j) => (j === i ? { ...x, featured: e.target.checked } : x)))} /> Featured
                </label>
                <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0 }} aria-label="Move up"
                  onClick={() => { if (i > 0) { const n = storeItems.slice(); [n[i - 1], n[i]] = [n[i], n[i - 1]]; setStores(n); } }}>↑</button>
                <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0 }} aria-label="Move down"
                  onClick={() => { if (i < storeItems.length - 1) { const n = storeItems.slice(); [n[i + 1], n[i]] = [n[i], n[i + 1]]; setStores(n); } }}>↓</button>
                <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0, color: '#ff7a73' }}
                  onClick={() => setStores(storeItems.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            {storeItems.length < 80 && (
              <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px' }}
                onClick={() => setStores([...storeItems, { name: '', url: '', logo: null, visible: true, featured: false }])}>+ Add store</button>
            )}
            <p style={{ color: 'var(--hq-muted)', fontSize: 13 }}>Links must start with http:// or https:// — anything else is dropped on save. Logos: PNG/JPEG/WebP, transparent PNG looks best on the dark site.</p>
            <p style={{ color: 'var(--hq-muted)', fontSize: 13, marginTop: -6 }}>Featured stores show first on the site in a row of bigger tiles (in this list’s order). 2–4 looks best. A store must also be Shown to appear.</p>
          </div>
        </Panel>
      )}

      {tab === 'menu' && ( /* @@MENU_CAREERS_v89@@ */
        <MenuEditor menu={draft.menu} sections={draft.sections} onChange={(m) => setDraft((d) => ({ ...d, menu: m }))} />
      )}

      {tab === 'careers' && ( /* @@MENU_CAREERS_v89@@ */
        <CareersEditor careers={draft.careers} assets={assets} onChange={(cr) => setDraft((d) => ({ ...d, careers: cr }))} />
      )}

      {tab === 'clients' && (
        <Panel title="Clients served" show={draft.sections.clients} onToggle={(v) => toggle('clients', v)}>
          <Field label="Heading" value={draft.clients.heading} max={100} onChange={(v) => up('clients', 'heading', v)} />
          <div style={S.grid2}>
            <label style={S.label}>Names — one per line ({draft.clients.names.length})
              <textarea className="hq-input" style={S.area} rows={18} value={draft.clients.names.join('\n')}
                onChange={(e) => up('clients', 'names', e.target.value.split('\n'))} />
            </label>
            <label style={S.label}>Festivals — one per line
              <textarea className="hq-input" style={S.area} rows={18} value={draft.clients.festivals.join('\n')}
                onChange={(e) => up('clients', 'festivals', e.target.value.split('\n'))} />
            </label>
          </div>
        </Panel>
      )}

      {tab === 'images' && (
        <Panel title="Images">
          <label className="hq-btn" style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', cursor: 'pointer' }}>
            {busy === 'upload' ? 'Uploading…' : 'Upload image'}
            <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
              onChange={async (e) => { await upload(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <p style={{ margin: 0, color: 'var(--hq-muted)', fontSize: 13 }}>PNG, JPEG or WebP up to 3 MB. Use them as section backgrounds or store logos. Images in use can’t be deleted.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {assets.map((a) => (
              <div key={a.id} style={{ border: '1px solid var(--hq-line)', borderRadius: 10, overflow: 'hidden', background: 'var(--hq-field)' }}>
                <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0b' }}>
                  <img src={a.url} alt={a.filename || ''} style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }} />
                </div>
                <div style={{ padding: 10, fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename || 'image'} · {Math.round(a.byte_size / 1024)} KB</span>
                  {a.in_use ? <span className="hq-pill hq-on">in use</span>
                    : <button type="button" className="hq-signout" style={{ ...S.small, width: 'auto', marginTop: 0 }} onClick={() => removeAsset(a)}>Delete</button>}
                </div>
              </div>
            ))}
            {assets.length === 0 && <p className="hq-empty" style={{ padding: 0 }}>No images uploaded yet — the built-in warehouse photos are used.</p>}
          </div>
        </Panel>
      )}

      {tab === 'inquiries' && <WebsiteInquiries /> /* @@FORM_SPAM_v96@@ */}

      {tab === 'traffic' && ( /* @@SITE_ANALYTICS_v92@@ */
        <WebsiteTraffic gaId={(draft.analytics || {}).ga_id} onGaChange={(v) => setDraft((d) => ({ ...d, analytics: { ...(d.analytics || {}), ga_id: v } }))} />
      )}
    </div>
  );
}

